/**
 * Project Bobcat feature flags.
 *
 * Bobcat is steered by three `bobcat-*` LaunchDarkly flags (`BOBCAT_FLAGS`
 * below), all OFF by default, so the `project-bobcat` branch can be rebased,
 * merged or discarded without exposing a demo surface to anyone. The demo
 * itself is a flag flip. Every other Bobcat surface follows one of the three
 * through its consumer, or is built but unreleased and has no key at all
 * (`bobcat-unreleased.ts`).
 *
 * ## Why these keys are declared here and not read off `AvailableFeatureFlagKeys`
 *
 * `AvailableFeatureFlagKeys` (`@pypestream/feature-flags`) is AUTO-GENERATED
 * from whatever LaunchDarkly currently serves — `packages/feature-flags/builded/index.d.ts`
 * opens with "do not manually modify this auto-generated file!!". So a flag
 * cannot be added to that union by hand: it has to exist in LaunchDarkly first
 * and then be picked up by a codegen run.
 *
 * That ordering would otherwise block every Bobcat win on an LD change, so the
 * keys live here as their own union and `resolveBobcatAccess` takes a plain
 * `Record<string, boolean>` — exactly what `resolvePypeNextAccess` already
 * does. A key that LaunchDarkly has never heard of is simply absent from the
 * map, which resolves to `denied`. That is the desired default, so an
 * un-created flag fails closed rather than breaking the build.
 *
 * ## Do NOT constrain these keys with `satisfies AvailableFeatureFlagKeys`
 *
 * It is the obvious-looking hardening and it would break the build on any
 * machine whose flag types are stale. `packages/feature-flags/builded/` is
 * **gitignored** (`packages/feature-flags/.gitignore`: `builded/*`), so
 * `AvailableFeatureFlagKeys` is a LOCAL artifact regenerated per machine by
 * `yarn build` in that package — which needs `LAUNCH_DARKLY_NODE_SDK`. A
 * checkout that has never run that codegen, or ran it before these flags
 * existed, would fail to compile on the `satisfies` clause rather than
 * degrading gracefully. Keeping the union local makes this file machine- and
 * codegen-independent, which is the property that matters for a demo branch.
 *
 * The cost is that nothing checks these strings: a typo
 * (`'bobcat-agent-veiw'`) compiles, lints and passes every test that
 * derives its expectation from `BOBCAT_FLAGS` itself. `bobcat-flags.test.ts`
 * pins the registry by hand (the three names, and the LaunchDarkly string each
 * resolver case reads), which catches a typo in one of THEM; a new key is
 * checked by nothing until it is added there. The failure is silent and
 * one-directional: the flag reads as ON in LaunchDarkly while the surface
 * stays hidden, with no error anywhere. **Cross-check any new key against the
 * LaunchDarkly dashboard by eye.**
 *
 * ## The flag must be available to client-side SDKs
 *
 * The browser SDK only receives flags marked *available to client-side SDKs* in
 * LaunchDarkly — which is why the type codegen passes `clientSideOnly: true`
 * (`packages/feature-flags/src/index.ts`). A flag created WITHOUT that toggle
 * is simply absent from `ldClient.allFlags()`, so it resolves to `denied` even
 * when switched ON. That is the most likely reason a demo flip appears to do
 * nothing — check the toggle before debugging this file.
 *
 * A flag that has NOT been created in LaunchDarkly is absent from the map and
 * resolves to `denied`, which is exactly the intended default.
 *
 * `bobcat-grid-grouping` used to be listed here. It was deleted on 2026-09-05
 * along with Win 1.1: node grouping is already complete in Pro Studio, so the
 * win had nothing to build and the flag never gained a consumer. A registry
 * entry for a flag that no longer exists in LaunchDarkly is worse than no
 * entry — `__bobcat__.status()` would report a phantom flag as permanently
 * off.
 *
 * `bobcat-agent-view` used to be listed here (Proposal 1's read-only Agent
 * View tab, Win 1.2). It was deleted on 2026-09-23: the Build-Stage canvas
 * reached feature parity with it (real-group rendering, the "Ungrouped: N"
 * indicator) and Elana confirmed the transitional page could go — "the
 * agent view can go away - the whole design is going to match mostafa's
 * design and the 5 step process." Same reasoning as `bobcat-grid-grouping`'s
 * removal: a registry entry for a flag with no surface left to gate is worse
 * than no entry.
 */

/**
 * One key per Bobcat control that is switched in LaunchDarkly. There are three,
 * and Bobcat has only these: the master `fiveStageNav`, and the two stages
 * that are released apart from it, `validateStage` and `observeStage`. Add a
 * key here only for a new control the release is steered by, and say in its
 * comment why it cannot ride one of the three.
 *
 * Every other Bobcat surface has no key (PSTUDIO-4636 removed the 21 it had):
 * 18 are part of the master and their consumers read `fiveStageNav`; the Test
 * View tab follows `validateStage`; guided setup follows the master,
 * Validate and Observe through `useGuidedSetupEnabled`; auto-publish is
 * built but not released and lives in `bobcat-unreleased.ts`.
 */
export const BOBCAT_FLAGS = {
  /**
   * The Design/Build/Validate/Deploy/Observe stage nav that replaces the
   * single "Agent Workflow" tab in the header, on the real Protolab
   * prototype's own model (`prostudio-build-flow-layouts.html`, checked live
   * this session — see "PR Spec: 5-Stage Nav"). Phase 1 only: the tab
   * control itself, real Table View/Canvas re-nested under "Build", and
   * honest placeholders for the other four stages — not their real content,
   * which is separately-phased, separately-flagged work.
   *
   * This is THE master (PSTUDIO-4636). It changes the primary navigation shell
   * of the whole Workflows page — the single biggest UI change in Project
   * Bobcat so far — and with this OFF the header and page render EXACTLY as
   * they did before this flag existed: additive, not destructive.
   *
   * It also opens what used to have a key of its own each, which consumers now
   * read as `fiveStageNav`: the Build canvas and its editing, node detail
   * panel, agent sidebar and routing highlight; the cell property editor and
   * its parameter-input drill-down and Manage Intents; Resource Library
   * drag-and-drop; the agent vocabulary; conversational create, agent
   * templates and the Knowledge Agent intake and global instructions; the
   * automatic agent grouping and use-case agent numbering that write group
   * rows; the solution assistant and AI Action Assist, which call Pro Studio's
   * own `/ai/*` routes. One of them cannot be switched off alone: pulling a
   * single feature means switching this master off, which takes the nav with
   * it, or a code change.
   *
   * `routes.actions`'s own nav tab ("Actions") was left unchanged by this
   * flag until the 2026-09-29 status check placed it: with the flag on, it
   * is an icon on the Build bar that opens Build's Actions view, the tab is
   * hidden, and the old route redirects there — as Files did before it.
   */
  fiveStageNav: 'bobcat-five-stage-nav',
  /**
   * The Validate stage built from the testing tool's pieces: a live Test
   * chat over the Engagement API socket (`@pypestream/testview`), simulated
   * scenarios, every reply scored on the Generative AI Evaluation Rubric
   * through `/ai/validate/*`, and a run history with trace links.
   *
   * Its own flag: it is a new LLM surface on its own server routes, and it
   * opens real conversations on a solution's channels. Needs `fiveStageNav`
   * (it is the Validate stage's content). Off, the stage shows as closed. It
   * also covers the Test View tab beside Table View, which used to have a key
   * of its own (`bobcat-test-view`, PSTUDIO-4636).
   */
  validateStage: 'bobcat-validate-stage',
  /**
   * The Observe stage (`pages/observe-stage.tsx`): the project's pinned
   * Analytics dashboards and a link out to Analytics. Its own flag so it can
   * be turned on for demos, or pulled, apart from the five-stage nav, the same
   * way Validate is (release page "Bobcat October Release", PSTUDIO-4583).
   * Off, the Observe nav item shows grayed out as "coming soon". Needs
   * `fiveStageNav`.
   */
  observeStage: 'bobcat-observe-stage',
} as const;

export type BobcatFlagName = keyof typeof BOBCAT_FLAGS;
export type BobcatFlagKey = (typeof BOBCAT_FLAGS)[BobcatFlagName];

/**
 * Tri-state, for the same reason `resolvePypeNextAccess` is tri-state:
 * `ctx.featureFlags` is `undefined` until LaunchDarkly answers, which is after
 * first render, and `WithLoadingState` renders a page during loading rather
 * than holding it back. Collapsing `undefined` into "off" (the `?? false` most
 * flag consumers use) makes a reload or a deep link redirect straight back out
 * of the surface — which on stage reads as the demo being broken.
 *
 * `pending` is bounded: a LaunchDarkly failure reports an empty flag map rather
 * than staying silent (PSTUDIO-3768), so this resolves to `denied` instead of
 * hanging forever.
 */
export type BobcatAccess = 'pending' | 'allowed' | 'denied';

export const resolveBobcatAccess = (
  flag: BobcatFlagName,
  featureFlags?: Record<string, boolean>
): BobcatAccess => {
  if (!featureFlags) {
    return 'pending';
  }

  // `=== true` and not a truthiness check, because the boolean in
  // `FeatureFlagsType` is an assumption the runtime does not honour: the values
  // come from `ldClient.allFlags()`, and this repo already has a STRING-valued
  // flag the generated type calls a boolean — `ps-agent-compiler-errors` serves
  // 'off' | 'on' | 'bot_only' (see `agent-compiler-mode.ts`, which exists to
  // parse exactly that). A Bobcat flag accidentally created as a multivariate
  // flag serving the string "off" is therefore truthy, and a truthiness check
  // would open the surface — failing OPEN, the opposite of every other
  // guarantee documented above.
  return featureFlags[BOBCAT_FLAGS[flag]] === true ? 'allowed' : 'denied';
};
